import { z } from 'zod';

import { AnswerVisibilityPrefSchema } from '../answer-visibility-pref/answer-visibility-pref';

/**
 * One row of the member's privacy screen (P12d): a field, a context, whether it
 * is currently shown, and whether the member is allowed to change that.
 *
 * `visible` is the **resolved** answer, not the stored preference — a switch
 * reading "off" because the staff default is off is the same fact to the member
 * as one they turned off themselves, and it has to render the same way.
 *
 * **A forced context is listed only beside a live one** (D5): a question the
 * member controls in one context arrives with both, the locked one marked
 * `isControllable: false` so the screen draws a lock with a reason rather than
 * an off switch — which would tell them they could turn it on. A question forced
 * in both contexts is not listed at all; the screen is for decisions that are
 * the member's.
 */
export const VisibilityPrefRowSchema = AnswerVisibilityPrefSchema.extend({
  /** Already resolved into the caller's locale by the api (N5). */
  label: z.string().min(1),
  /** Whether the screen renders a switch, or a lock. */
  isControllable: z.boolean(),
  /**
   * The staff-built section the question belongs to (D4), so the privacy screen
   * groups its rows under the same headings the member filled the wizard in —
   * forty questions in one flat list is what made "which one is my city?" a
   * question at all.
   */
  sectionKey: z.string().min(1),
  /** The section's title in the caller's locale (N5). */
  sectionTitle: z.string().min(1),
});

export type VisibilityPrefRow = z.infer<typeof VisibilityPrefRowSchema>;
